import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Send, ArrowLeft, Mail, MessageCircle, Users } from 'lucide-react';

export default function NewMessage({ setActiveSection }) {
  const [recipientId, setRecipientId] = useState('');
  const [message, setMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/catalog/messages/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (!res.ok) throw new Error('Failed to fetch users');
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleSend = async () => {
    if (!recipientId || !message.trim()) return;

    setIsSending(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/catalog/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          message_from: user.id,
          message_to: parseInt(recipientId),
          message: message.trim()
        }),
      });

      if (!response.ok) throw new Error('Failed to send message');

      const { threadId } = await response.json();
      
      setTimeout(() => {
        setActiveSection(`message-thread/${threadId}`);
      }, 500);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setActiveSection('messages-inbox')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 bg-white px-3 py-2 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <span className="text-sm font-medium text-gray-500">New Message</span>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Mail size={18} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white">Compose Message</h1>
                <p className="text-blue-100 text-sm">Send a message to team members</p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            <AnimatePresence>
              {error && (
                <motion.div 
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 rounded-lg flex items-start gap-3 text-sm"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Unable to send message</p>
                    <p className="mt-0.5">{error}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-4">
              {/* Recipient Field */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Select Recipient
                </label>
                {loading ? (
                  <div className="border border-gray-300 rounded-lg px-4 py-3 bg-gray-50 text-gray-500 flex items-center gap-3 text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                    Loading recipients...
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={recipientId}
                      onChange={(e) => setRecipientId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 pl-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white text-sm"
                    >
                      <option value="">Choose a recipient...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {`${user.first_name || ''} ${user.last_name || ''}`.trim()} • {user.role || 'No Role'}
                        </option>
                      ))}
                    </select>
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <User size={16} />
                    </div>
                  </div>
                )}
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Your Message
                </label>
                <div className="relative">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none text-sm"
                    placeholder="Write your message here..."
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                    {message.length} characters
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <div>
                <button
                  onClick={handleSend}
                  disabled={!recipientId || !message.trim() || isSending}
                  className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 font-medium text-sm transition-colors ${
                    !recipientId || !message.trim() || isSending
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isSending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                    </>
                  )}
                </button>

                <p className="text-center text-gray-500 text-xs mt-2">
                  {!recipientId || !message.trim() 
                    ? "Fill in all fields to send your message" 
                    : "Your message will be delivered instantly"
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}